import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../../queries';
import { DateInput } from '../../../../components-v2/DateInput';
import { TextArea } from '../../../../components-v2/TextArea';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Badge } from '../../../../components/dataBoards/Badge';
import { DonationCampaignSearch } from '../../../../components/dataBoards/donations/DonationCampaignSearch';
import { ImageFileChooserModal } from '../../../../components/ImageFileChooserModal';
import { ImagePreview } from '../../../../components/ImagePreview';
import { ImageUploadButtons } from '../../../../components/ImageUploadButtons';
import { MediaChooserModal } from '../../../../components/MediaChooser';
import { DefaultHighResHorizontalPixabayImageSearchOptions } from '../../../../components/MediaChooser/models';
import { IModalProps } from '../../../../components/Modal';
import { Separator } from '../../../../components/Separator';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { Toggle } from '../../../../components/Toggle';
import { white } from '../../../../styles/colors';
import { styleSheet } from '../styles';
import { DonationSurveyFormData } from '../utils';

export const PageSetup: React.FC<{ survey: Api.IDonationSurvey }> = ({ survey }) => {
	const isArchived = !!survey?.archivedDate;
	const {
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<DonationSurveyFormData>();
	const formValues = watch();
	const errorMessages = useErrorMessages();
	const onFreeImageChooserRequestCloseRef = React.useRef<(img?: Api.IFileAttachment, cancel?: boolean) => void>(null);
	const [freeImageModalProps, setFreeImageModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: (result, cancel) => onFreeImageChooserRequestCloseRef.current?.(result, cancel),
	});
	const [fileInputModalOpen, setFileInputModalOpen] = React.useState(false);

	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: (error: Api.IOperationResultNoValue): void => {
			errorMessages.pushApiError(error);

			toast.error('Failed to upload image');
		},
		onSuccess: result => {
			setValue('image', result[0]);
			setValue('imageSource', Api.IAttachmentUploadSource.Upload);
			setFileInputModalOpen(false);
		},
	});

	function onClickFreeImage() {
		setFreeImageModalProps(value => {
			return {
				...value,
				isOpen: true,
			};
		});
	}

	const onFreeImageChooserRequestClose = React.useCallback(
		(imageFile?: Api.IFileAttachment, cancel?: boolean) => {
			if (cancel || !imageFile) {
				setFreeImageModalProps(value => {
					return {
						...value,
						isOpen: false,
					};
				});
				setValue('image', null);
				setValue('imageSource', null);
				return;
			}
			setValue('image', imageFile);
			setValue('imageSource', Api.IAttachmentUploadSource.Pixabay);
			setFreeImageModalProps(value => {
				return {
					...value,
					isOpen: false,
				};
			});
		},
		[setValue]
	);

	const handleFileInputModalClose = (val?: File | null, cancel?: boolean) => {
		if (val && !cancel) {
			imageAttachmentMutation.mutate({ files: [val] });
		}
	};

	onFreeImageChooserRequestCloseRef.current = onFreeImageChooserRequestClose;

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Page Setup</h3>
			<div className={css(styleSheet.formSection)}>
				<div className={css(styleSheet.formField)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<label htmlFor={'cause-details-input'} className={css(styleSheet.formFieldLabel)}>
							Cause Details
						</label>
						<span className={css(styleSheet.formFieldDescription)}>
							This will show up as a description for your donation page
						</span>
					</div>

					<div>
						<Controller
							name='causeDetails'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<TextArea
									styles={[styleSheet.causeDetailsTextArea]}
									disabled={isArchived}
									id={'cause-details-input'}
									placeholder=''
									onChange={fieldProps.onChange}
									value={fieldProps.value}
								/>
							)}
						/>

						{errors.causeDetails?.message && (
							<span className={css(styleSheet.errorMessage)}>{errors.causeDetails?.message}</span>
						)}
					</div>
				</div>

				<Separator />

				<div className={css(styleSheet.formField)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
							<label htmlFor={'cause-details-input'} className={css(styleSheet.formFieldLabel)}>
								Allows Campaign Specific Donations
							</label>

							<Controller
								name='allowProgramSpecificDonations'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Toggle
										id='allow-program-specific-donations-toggle'
										isOn={fieldProps.value}
										onToggleCheckChanged={fieldProps.onChange}
										uncheckedColor={THEME.colors.background.dark}
									/>
								)}
							/>
						</div>

						<span className={css(styleSheet.formFieldDescription)}>
							Search for existing campaigns or add a new campaign by typing a name and selecting &quot;Add&quot;
						</span>
					</div>

					<div className={css(styleSheet.relative)}>
						{formValues.programSpecificDonation && formValues.programSpecificDonation.length > 0 && (
							<div className={css(styleSheet.selectedCampaignsList)}>
								{formValues.programSpecificDonation.map(campaign => (
									<div key={campaign.id} className={css(styleSheet.campaignBadgeWrapper)}>
										<Badge text={campaign.name} foregroundColor={campaign.foregroundColor || '#0066CC'} />
										{!isArchived && formValues.allowProgramSpecificDonations && (
											<button
												className={`${css(styleSheet.campaignBadgeRemove)} campaign-badge-remove`}
												onClick={() => {
													const newCampaigns = formValues.programSpecificDonation.filter(c => c.id !== campaign.id);
													setValue('programSpecificDonation', newCampaigns);
												}}
												type='button'
												aria-label={`Remove ${campaign.name}`}
											>
												<XIcon width={10} height={10} color={white} />
											</button>
										)}
									</div>
								))}
							</div>
						)}

						<Controller
							name='programSpecificDonation'
							control={control}
							render={({ field: { ref, value, ...fieldProps } }) => {
								return (
									<DonationCampaignSearch
										initialSearchFragment=''
										onSelectDonationCampaign={(campaign: Api.IDonationCampaign) => {
											const currentCampaigns = value || [];
											if (!currentCampaigns.some(c => c.id === campaign.id)) {
												const newCampaigns = [...currentCampaigns, campaign];
												fieldProps.onChange(newCampaigns);
											}
										}}
										onCreateCampaign={(campaign: Api.IDonationCampaign) => {
											const currentCampaigns = value || [];
											if (!currentCampaigns.some(c => c.id === campaign.id)) {
												const newCampaigns = [...currentCampaigns, campaign];
												fieldProps.onChange(newCampaigns);
											}
										}}
										onClear={() => {
											// No action needed on clear
										}}
										disabled={isArchived || !formValues.allowProgramSpecificDonations}
										className={css(styleSheet.campaignSearchWrapper)}
										dropdownClassName={css(styleSheet.campaignSearchDropdown)}
									/>
								);
							}}
						/>

						{errors.programSpecificDonation?.message && (
							<span className={css(styleSheet.errorMessage)}>{errors.programSpecificDonation?.message}</span>
						)}
					</div>
				</div>

				<Separator />

				<div className={css(styleSheet.formField)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
							<label htmlFor={'cause-details-input'} className={css(styleSheet.formFieldLabel)}>
								Set Donation End Date
							</label>

							<Controller
								name='hasExpirationDate'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Toggle
										id='has-expiration-date-toggle'
										isOn={fieldProps.value}
										onToggleCheckChanged={fieldProps.onChange}
										uncheckedColor={THEME.colors.background.dark}
									/>
								)}
							/>
						</div>

						<span className={css(styleSheet.formFieldDescription)}>
							This will disable donations after the specified date
						</span>
					</div>

					<div>
						<Controller
							name='expirationDate'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<DateInput value={fieldProps.value} onChange={fieldProps.onChange} />
							)}
						/>

						{errors.expirationDate?.message && (
							<span className={css(styleSheet.errorMessage)}>{errors.expirationDate?.message}</span>
						)}
					</div>
				</div>

				<Separator />

				<div className={css(styleSheet.formField)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<label className={css(styleSheet.formFieldLabel)}>Upload Image</label>

						<span className={css(styleSheet.formFieldDescription)}>
							This will be displayed as the main image for your survey
						</span>
					</div>

					<div>
						{formValues.image && (
							<ImagePreview
								fileName={formValues.image?.fileName}
								fileSize={formValues.image?.fileSize}
								source={formValues.imageSource}
								onRemove={() => setValue('image', null)}
								url={formValues.image?.url}
							/>
						)}
						{!formValues.image && (
							<ImageUploadButtons
								hasImage={Boolean(formValues.image)}
								onClickFreeImage={() => onClickFreeImage()}
								onClickMedia={() => setFileInputModalOpen(true)}
							/>
						)}
					</div>
				</div>

				<ImageFileChooserModal
					modalProps={{
						isOpen: fileInputModalOpen,
						onRequestClose: handleFileInputModalClose,
					}}
				/>

				{freeImageModalProps.isOpen ? (
					<MediaChooserModal
						imageOnly={true}
						modalProps={freeImageModalProps}
						onSave={onFreeImageChooserRequestClose}
						useBasicSearch={false}
						imageSearchOptions={DefaultHighResHorizontalPixabayImageSearchOptions}
					/>
				) : null}
			</div>
		</section>
	);
};
